import { useState } from "react";
import { config, renderTokens } from "@/config/eventConfig";
import { usePersistentState } from "@/hooks/usePersistentState";

const inlineInput =
  "inline-block bg-transparent border-b-2 border-ink/40 focus:border-orange focus:outline-none px-1 text-ink placeholder:text-ink/30";
const m = config.recipe_matcher;
const t = (s) => renderTokens(s);
const doc = (id) => config.recipeDocs?.[id] || {};
const recipeName = (id) =>
  doc(id).title || (config.recipes.find((r) => r.id === id) || {}).name || id;

// The Build landing: the scoping sentence as a thinking prompt, then the full
// recipe gallery and problem table as browsable guidance. Nothing is required.
export const BuildScoping = () => {
  const [subject, setSubject] = usePersistentState("build.subject", "");
  const [who, setWho] = usePersistentState("build.who", "");
  const [done, setDone] = usePersistentState("build.done", "");
  const [chosen, setChosen] = usePersistentState("build.recipeId", null);
  const [tableOpen, setTableOpen] = useState(true);

  const whoTriggers =
    who.toLowerCase().includes("the team") ||
    /\b(everyone|staff|us)\b/.test(who.toLowerCase());

  const galleryRecipes = config.recipes.filter((r) => r.id !== m.fallback_recipe);

  return (
    <div data-testid="build-scoping">
      {/* The scoping sentence, now a thinking prompt for the table */}
      <p className="mono-label text-ink/60 mb-4">The sentence</p>
      <p className="text-2xl sm:text-3xl leading-relaxed">
        We are building{" "}
        <input
          data-testid="scope-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="what"
          className={`${inlineInput} w-44 sm:w-56`}
        />{" "}
        for{" "}
        <input
          data-testid="scope-who"
          value={who}
          onChange={(e) => setWho(e.target.value)}
          placeholder="a person or role"
          className={`${inlineInput} w-44 sm:w-56`}
        />
        , and done looks like{" "}
        <input
          data-testid="scope-done"
          value={done}
          onChange={(e) => setDone(e.target.value)}
          placeholder="a 15-second demo"
          className={`${inlineInput} w-56 sm:w-72`}
        />
        .
      </p>

      <div className="mt-4 space-y-3">
        {whoTriggers && (
          <p data-testid="who-hint" className="callout-guidance text-teal">
            Name a person or a role. "For Muriel, on the first Monday of every
            month" is an answer. "For the team" is not.
          </p>
        )}
        <p data-testid="done-hint" className="callout-guidance text-teal">
          Something you could show somebody in fifteen seconds.
        </p>
      </div>

      <p data-testid="scope-say-aloud" className="text-ink/70 mt-4 max-w-2xl">
        Say this out loud at your table before you pick a recipe. If you cannot
        finish the sentence yet, that is the conversation to have first.
      </p>

      {/* Recipe gallery, all options visible immediately */}
      <div className="mt-14">
        <p className="mono-label text-ink/60 mb-4">Pick a recipe (or, create your own recipe)</p>
        <div
          data-testid="recipe-gallery"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {galleryRecipes.map((r) => {
            const d = doc(r.id);
            const sel = chosen === r.id;
            return (
              <button
                key={r.id}
                data-testid={`recipe-card-${r.id}`}
                onClick={() => setChosen(r.id)}
                className={`text-left border p-5 h-full flex flex-col transition-colors duration-150 ${
                  sel ? "border-teal" : "border-sand hover:border-orange"
                }`}
              >
                <p className="font-display text-xl leading-tight mb-2">
                  {recipeName(r.id)}
                </p>
                <p className="text-sm text-ink/70 flex-1">{t(d.description)}</p>
                <span className="mono-label text-ink/40 mt-3">
                  {d.minutes} MINUTES
                </span>
                {sel && <span className="mono-label text-teal mt-2">Selected</span>}
              </button>
            );
          })}
        </div>

        {/* Fallback, kept outside the grid with its badge */}
        <button
          data-testid="fallback-card"
          onClick={() => setChosen(m.fallback_recipe)}
          className={`w-full text-left mt-4 border p-5 transition-colors duration-150 ${
            chosen === m.fallback_recipe
              ? "border-teal"
              : "border-sand hover:border-orange"
          }`}
        >
          <span className="inline-block bg-teal text-white mono-label px-3 py-1 mb-3">
            {m.fallback_badge}
          </span>
          <p className="font-display text-2xl leading-none mb-1">
            {recipeName(m.fallback_recipe)}
          </p>
          <p className="text-sm text-ink/70">
            {t(doc(m.fallback_recipe).description)}
          </p>
          {chosen === m.fallback_recipe && (
            <span className="mono-label text-teal mt-2 block">Selected</span>
          )}
        </button>

        <p data-testid="gallery-cta" className="text-sm text-ink/70 mt-4">
          {chosen
            ? `Selected: ${recipeName(chosen)}. Press Next to open its guide.`
            : "Talk about which one fits, tap it, then press Next to open its guide."}
        </p>
      </div>

      {/* Plain-language problem table, expanded, a reading aid */}
      <div className="mt-14">
        <button
          data-testid="problem-table-toggle"
          onClick={() => setTableOpen((v) => !v)}
          className="mono-label text-ink/60 mb-4 block text-left"
        >
          {m.table_heading}
        </button>
        {tableOpen && (
          <div data-testid="problem-table" className="border border-sand">
            {m.problem_lines.map((line, i) => (
              <button
                key={i}
                data-testid={`problem-line-${i}`}
                onClick={() => setChosen(line.recipe)}
                className={`w-full grid grid-cols-1 sm:grid-cols-2 gap-2 text-left px-5 py-4 border-b border-sand last:border-b-0 transition-colors duration-150 ${
                  chosen === line.recipe ? "bg-cream" : "hover:bg-cream/60"
                }`}
              >
                <span className="text-ink/80">"{line.text}"</span>
                <span className="text-ink/60 sm:text-right">
                  {recipeName(line.recipe)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
